import {
  ChangeEvent,
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  FocusEventHandler,
  ReactElement,
  forwardRef,
  isValidElement,
  useId,
  useState,
  useRef,
  useEffect,
  useMemo,
  Ref,
} from "react";
import { useTrackingId, useMergeRefs } from "../../hooks";
import {
  DataTrackingId,
  LayoutUtilProps,
  MaxLengthCounterProps,
} from "../../types";
import FieldLabel, { FieldLabelProps } from "../FieldLabel";
import { Helper, HelperProps } from "../../internal/components";
import { warnDeprecatedErrorUsage } from "../FieldMessage";
import cx from "classnames";
import styles from "./Textarea.module.scss";
import {
  useLayoutPropsUtil,
  useOptionallyControlledState,
} from "../../internal/hooks";
import { TextareaAutosizeConditionalProps, TextareaState } from "./types";
import TextareaAutosize from "react-textarea-autosize";
import {
  childrenToString,
  stripInlineMarkdown,
  warnOnce,
} from "../../internal/functions";

/**
 * Props for the Textarea component
 */
export type TextareaProps = Omit<
  ComponentPropsWithoutRef<"textarea">,
  "onChange"
> &
  LayoutUtilProps &
  DataTrackingId &
  MaxLengthCounterProps &
  TextareaAutosizeConditionalProps &
  Pick<FieldLabelProps, "moreInfo"> & {
    /**
     * Error state for the field. Pass `true` to indicate error styling without a message.
     * Pass a string, string[], or ReactElement (deprecated) for error messages.
     */
    error?: boolean | string | ReactElement | string[];
    /**
     * Hint text displayed below the textarea
     */
    hint?: ReactElement | string;
    /**
     * Description text displayed below the textarea
     */
    description?: ReactElement | string;

    // TODO(v4.0.0): make label required and type string
    /**
     * Label for the textarea.
     * Omitting `label` is deprecated — it will be required in v4.0.0. Use `hideLabel` to visually hide it.
     * Passing `ReactNode` is deprecated — use a plain string with inline markdown instead.
     */
    label?: string | ReactElement;
    /**
     * When `true`, hides the visible label and moves it to `aria-label` on the textarea.
     * Requires `label` to be set — the label is always needed for accessibility.
     * @default false
     */
    hideLabel?: boolean;

    /**
     * Sets textarea's element no user-controllable method for resizing
     * @default false
     */
    disableResize?: boolean;

    /**
     * Fires on each change of textarea's value
     * @param e The change event
     * @param state Object containing the current value
     */
    onChange?: (
      e: ChangeEvent<HTMLTextAreaElement>,
      state?: TextareaState,
    ) => void;

    /**
     * Props passed to the internal label component.
     */
    labelProps?: FieldLabelProps;
  } & {
    /**
     * @deprecated No longer used. Error messages always use `aria-live="assertive"`.
     */
    errorAriaLive?: HelperProps["errorAriaLive"];
    /**
     * Warning message(s) to display. Supports a single string or an array of strings.
     */
    warning?: string | string[];
  };

/**
 * Textarea component for multi-line text input with advanced features.
 *
 * Features:
 * - Character counter with maxLength validation
 * - Auto-height adjustment based on content
 * - Error state handling with custom error messages
 * - Hint and description text support
 * - Disable resize functionality
 * - Accessible with proper ARIA attributes
 * - Label association and required field support
 * - Focus and blur event handling
 * - Supports layout utilities for positioning and spacing
 * - SSR-safe auto-height implementation
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <Textarea
 *   label="Description"
 *   placeholder="Enter your description here"
 *   onChange={(e, state) => console.log('Value:', state?.value)}
 * />
 *
 * @example
 * <Textarea
 *   label="Bio"
 *   showCounter
 *   maxLength={500}
 *   hint="Tell us about yourself"
 * />
 */
export const Textarea = forwardRef(
  (props: TextareaProps, ref: Ref<HTMLTextAreaElement>) => {
    const { aiMark, ...labelPropsWithoutAiMark } = props.labelProps || {};

    const data = {
      label: childrenToString(props.label),
      labelProps: labelPropsWithoutAiMark,
      prefix: childrenToString(props.prefix),
      hint: childrenToString(props.hint),
      description: childrenToString(props.description),
    };

    const trackingId = useTrackingId({
      name: "Textarea",
      data,
      hasOverride: !!props["data-tracking-id"],
    });

    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      value: valueProp,
      defaultValue: defaultValueProp,
      className,
      disableResize,
      error,
      errorAriaLive: _errorAriaLive,
      hint,
      description,
      label,
      moreInfo,
      onChange,
      required,
      showCounter,
      onFocus,
      onBlur,
      id: idProp,
      maxLength,
      autoHeight,
      minRows,
      maxRows,
      labelProps,
      placeholder,
      warning,
      style,
      hideLabel = false,
      ...rest
    } = componentProps;

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [isTyping, setIsTyping] = useState(false);
    const [ssr, setSSR] = useState(true);

    // Use the optionally controlled state for value
    const [value, setValue] = useOptionallyControlledState<typeof valueProp>({
      controlledValue: valueProp,
      defaultValue: defaultValueProp,
    });

    const length = useMemo(() => {
      if (
        typeof value === "string" ||
        typeof value === "number" ||
        Array.isArray(value)
      ) {
        return String(value).length;
      }
      return 0;
    }, [value]);

    useEffect(() => {
      setSSR(false);
    }, []);

    const onChangeHandler: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
      setValue(e.target.value);
      onChange?.(e, { value: e.target.value });
      if (!isTyping) {
        setIsTyping(true);
      }
    };

    const blurHandler: FocusEventHandler<HTMLTextAreaElement> = (e) => {
      onBlur?.(e);
      if (isTyping) {
        setIsTyping(false);
      }
    };

    const labelUid = useId();
    const id = idProp ?? labelUid;

    const helperUid = useId();
    const moreInfoUid = useId();

    warnDeprecatedErrorUsage("Textarea", error);

    // TODO(v4.0.0): remove once label is required string
    if (label === undefined) {
      warnOnce(
        "Textarea: label will be required in v4.0.0. Use hideLabel to visually hide the label.",
      );
    } else if (typeof label !== "string") {
      warnOnce(
        "Textarea: Passing ReactNode to label is deprecated. Use a plain string with inline markdown instead.",
      );
    }

    const errorMessages =
      typeof error === "boolean" || error === undefined ? undefined : error;

    const hasHelperText =
      hint || showCounter || errorMessages || warning || description;

    const ariaDescribedBy =
      [hasHelperText && helperUid, moreInfo && moreInfoUid]
        .filter(Boolean)
        .join(" ") || undefined;

    const textareaClassNames = cx([styles["textarea"]], className, {
      [styles["error"]]: error,
      [styles["disable-resize"]]: disableResize,
      [styles["disabled"]]: rest.disabled,
      [styles["readonly"]]: rest.readOnly,
      [styles["has-cols"]]: rest.cols != null,
    });

    const styleCombined = { ...style, ...layoutStyles };

    const shouldUseAutosize = autoHeight && !ssr;

    const TextareaComponent = shouldUseAutosize ? TextareaAutosize : "textarea";

    return (
      <div
        className={textareaClassNames}
        data-anv="textarea"
        style={styleCombined}
      >
        {/* TODO(v4.0.0): simplify to !hideLabel once label is required */}
        {label && !hideLabel && (
          <FieldLabel
            htmlFor={id}
            moreInfo={moreInfo}
            moreInfoId={moreInfo ? moreInfoUid : undefined}
            required={required}
            {...labelProps}
          >
            {label}
          </FieldLabel>
        )}

        <TextareaComponent
          rows={3}
          onChange={onChangeHandler}
          onFocus={onFocus}
          onBlur={blurHandler}
          ref={useMergeRefs([ref, textareaRef])}
          required={required}
          maxLength={maxLength}
          id={id}
          placeholder={placeholder}
          value={value ?? ""}
          data-tracking-id={trackingId}
          {...rest}
          {...(shouldUseAutosize ? { minRows, maxRows } : {})}
          aria-label={
            // TODO(v4.0.0): remove typeof check — label will always be string
            hideLabel && typeof label === "string"
              ? stripInlineMarkdown(label)
              : label
                ? undefined
                : placeholder
          }
          aria-describedby={ariaDescribedBy}
          aria-invalid={error ? !!error : undefined}
        />
        {hasHelperText ? (
          <Helper
            id={helperUid}
            hint={hint}
            maxLength={maxLength}
            inputLength={length}
            isTyping={isTyping}
            showCounter={showCounter}
            error={
              typeof errorMessages === "string" || Array.isArray(errorMessages)
                ? errorMessages
                : undefined
            }
            warning={warning}
            errorMessage={
              isValidElement(errorMessages) ? errorMessages : undefined
            }
            description={description}
          />
        ) : null}
      </div>
    );
  },
);
Textarea.displayName = "Textarea";
